import { composeAbortSignals } from '../http/compose-signals.js';
import { createLogger } from '../logging/logger.js';

const log = createLogger({ module: 'options-provider' });

/** @type {Record<string, unknown>} */
const EMPTY_VALUES = Object.freeze({});

/**
 * Applies mapOption to an options array. Returns a new frozen array.
 * Throws if mapOption throws (caller must handle).
 *
 * @param {import('../../contracts/forms.js').FieldOption[]} options
 * @param {((option: import('../../contracts/forms.js').FieldOption) => import('../../contracts/forms.js').FieldOption) | undefined} mapOption
 * @returns {ReadonlyArray<import('../../contracts/forms.js').FieldOption>}
 */
function applyMapOption(options, mapOption) {
  const mapped = mapOption ? options.map(mapOption) : options.slice();
  return Object.freeze(mapped);
}

/**
 * Creates an options provider that resolves options from static arrays,
 * async loaders, or both (hybrid). Supports debounce, abort, and
 * per-option transforms via mapOption.
 *
 * @param {import('../../contracts/forms.js').OptionsProviderConfig} config
 * @param {AbortSignal} [parentSignal]
 * @returns {import('../../contracts/forms.js').OptionsProviderHandle}
 */
export function createOptionsProvider(config, parentSignal) {
  const { loadOptions, mapOption, debounceMs = 0 } = config;

  /** @type {Array<(state: import('../../contracts/forms.js').OptionsState) => void>} */
  let listeners = [];
  let destroyed = false;
  let notifying = false;

  // Re-entrancy: deferred load args (last-call-wins coalescing)
  /** @type {{ query: string, values: Record<string, unknown> } | null} */
  let pendingReentrant = null;
  let reentrantScheduled = false;

  // Version counter for stale response detection
  let loadVersion = 0;

  // Current in-flight abort controller
  /** @type {AbortController | null} */
  let currentController = null;

  // Debounce state
  /** @type {ReturnType<typeof setTimeout> | null} */
  let debounceTimer = null;
  let hasLoadedOnce = false;

  // --- State ---
  /** @type {ReadonlyArray<import('../../contracts/forms.js').FieldOption>} */
  let options;
  let loading = false;
  /** @type {Error | null} */
  let error = null;

  // If parentSignal already aborted, start in destroyed state
  if (parentSignal && parentSignal.aborted) {
    destroyed = true;
    options = Object.freeze([]);
  } else {
    // Initialize with static options (applying mapOption)
    if (config.options && config.options.length > 0) {
      try {
        options = applyMapOption(config.options, mapOption);
      } catch (err) {
        options = Object.freeze([]);
        error = err instanceof Error ? err : new Error(String(err));
      }
    } else {
      options = Object.freeze([]);
    }
  }

  /** @returns {import('../../contracts/forms.js').OptionsState} */
  function getState() {
    return { options: /** @type {import('../../contracts/forms.js').FieldOption[]} */ (options), loading, error };
  }

  function notify() {
    if (destroyed) return;
    notifying = true;
    const state = getState();
    const snapshot = listeners.slice();
    for (const listener of snapshot) {
      try {
        listener(state);
      } catch (err) {
        log.error('Options provider subscriber threw', { data: err });
      }
    }
    notifying = false;
    // Re-entrant loads are deferred via microtask in load() — not processed here.
  }

  /**
   * @param {(state: import('../../contracts/forms.js').OptionsState) => void} listener
   * @returns {() => void}
   */
  function subscribe(listener) {
    if (destroyed) return () => {};
    listeners.push(listener);
    return () => {
      const idx = listeners.indexOf(listener);
      if (idx !== -1) listeners.splice(idx, 1);
    };
  }

  /**
   * @param {string} query
   * @param {Record<string, unknown>} values
   */
  function executeLoad(query, values) {
    if (destroyed || !loadOptions) return;

    // Abort previous in-flight
    if (currentController) {
      currentController.abort();
      currentController = null;
    }

    const version = ++loadVersion;
    const controller = new AbortController();
    currentController = controller;

    const signal = composeAbortSignals(parentSignal, controller.signal);

    // Set loading state
    loading = true;
    error = null;
    notify();

    loadOptions({ query, values, signal })
      .then(result => {
        if (version !== loadVersion) return; // stale
        if (destroyed) return;

        try {
          options = applyMapOption(result, mapOption);
        } catch (err) {
          // mapOption threw — treat like load error
          error = err instanceof Error ? err : new Error(String(err));
          loading = false;
          currentController = null;
          notify();
          return;
        }

        loading = false;
        error = null;
        currentController = null;
        notify();
      })
      .catch(err => {
        // Check abort FIRST (via signal.aborted, not err.name)
        if (signal && signal.aborted) {
          if (version !== loadVersion) return; // stale abort — ignore
          // Current abort (parentSignal or destroy with no replacement)
          loading = false;
          currentController = null;
          notify();
          return;
        }

        if (version !== loadVersion) return; // stale
        if (destroyed) return;

        // Real error — preserve last known options
        error = err instanceof Error ? err : new Error(String(err));
        loading = false;
        currentController = null;
        notify();
      });
  }

  /**
   * @param {string} [query]
   * @param {Record<string, unknown>} [values]
   */
  function load(query, values) {
    if (destroyed || !loadOptions) return;

    const q = query ?? '';
    const v = values ?? EMPTY_VALUES;

    // Re-entrancy guard: defer if we're inside a notify cycle
    if (notifying) {
      pendingReentrant = { query: q, values: v };
      if (!reentrantScheduled) {
        reentrantScheduled = true;
        queueMicrotask(() => {
          if (destroyed) { reentrantScheduled = false; pendingReentrant = null; return; }
          if (pendingReentrant) {
            const args = pendingReentrant;
            pendingReentrant = null;
            reentrantScheduled = false;
            load(args.query, args.values);
          } else {
            reentrantScheduled = false;
          }
        });
      }
      return;
    }

    // Debounce logic
    if (debounceMs > 0 && hasLoadedOnce) {
      if (debounceTimer !== null) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        executeLoad(q, v);
      }, debounceMs);
      return;
    }

    hasLoadedOnce = true;
    executeLoad(q, v);
  }

  function destroy() {
    if (destroyed) return;
    // (1) Set destroyed flag — prevents notifications during teardown
    destroyed = true;
    // (2) Clear subscribers
    listeners = [];
    // (3) Clear debounce timer
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    // (4) Abort in-flight request
    if (currentController) {
      currentController.abort();
      currentController = null;
    }
  }

  return { getState, subscribe, load, destroy };
}

/**
 * Convenience helper: subscribes and immediately invokes the listener
 * with the current state (exception-safe, matching notify semantics).
 *
 * Must not be called after destroy() — subscribe() returns a no-op
 * unsubscribe but the initial invoke still fires. Callers should
 * always call this before destroy().
 *
 * @param {import('../../contracts/forms.js').OptionsProviderHandle} provider
 * @param {(state: import('../../contracts/forms.js').OptionsState) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribeWithInitial(provider, listener) {
  const unsubscribe = provider.subscribe(listener);
  try {
    listener(provider.getState());
  } catch (err) {
    log.error('Options provider subscriber threw during initial invoke', { data: err });
  }
  return unsubscribe;
}
